import * as React from 'react';
import classnames from 'classnames';

import { sanitizeInput } from '@utils/AppUtil';

import { AI_GENERATED_TRANSLATION_LABEL } from './aiTranslationLabel';

import './style.scss';

export { AI_GENERATED_TRANSLATION_LABEL };

export interface ITranslationDisplayProps {
  originalText?: string;
  /** Rendered instead of `originalText` when the caller owns the original's markup, such as an email body. */
  originalContent?: React.ReactNode;
  sourceLanguageCode?: string | null;
  sourceLanguageLabel?: string | null;
  englishTranslation?: string | null;
  /** `lang` of the translated block. Staff views translate into English; resident views translate out of it. */
  translationLanguageCode?: string;
  /** `html` renders the translation as sanitized markup. */
  translationFormat?: 'text' | 'html';
  translated?: boolean | null;
  aiTranslationDisclaimer?: string;
  /** BCP 47 language of `aiTranslationDisclaimer`. Staff labels stay English. */
  aiTranslationDisclaimerLanguage?: string;
  className?: string;
}

export default function TranslationDisplay(props: ITranslationDisplayProps): JSX.Element {
  const {
    originalText,
    originalContent,
    sourceLanguageCode,
    sourceLanguageLabel,
    englishTranslation,
    translationLanguageCode = 'en',
    translationFormat = 'text',
    translated,
    aiTranslationDisclaimer = AI_GENERATED_TRANSLATION_LABEL,
    aiTranslationDisclaimerLanguage = 'en',
    className,
  } = props;

  const showLanguage = Boolean(sourceLanguageCode) && (translated === true || translated === false);
  const showTranslation = translated === true && Boolean(englishTranslation);
  const languageLabel = sourceLanguageLabel || sourceLanguageCode;
  const hasOriginalContent = originalContent !== undefined && originalContent !== null;
  const showOriginal = hasOriginalContent || Boolean(originalText);

  return (
    <div className={classnames('translation-display', className)}>
      {showLanguage && <div className="translation-display__language">{`Detected language: ${languageLabel}`}</div>}
      {showOriginal && (
        <div
          className={classnames('translation-display__original', {
            'translation-display__original--custom': hasOriginalContent,
          })}
          lang={sourceLanguageCode || undefined}
        >
          {hasOriginalContent ? originalContent : originalText}
        </div>
      )}
      {showTranslation && (
        <div className="translation-display__translation">
          <div className="translation-display__ai-label" lang={aiTranslationDisclaimerLanguage}>
            {aiTranslationDisclaimer}
          </div>
          {translationFormat === 'html' ? (
            <div
              className="translation-display__translated-text translation-display__translated-text--html"
              lang={translationLanguageCode}
              dangerouslySetInnerHTML={{ __html: sanitizeInput(englishTranslation) }}
            />
          ) : (
            <div className="translation-display__translated-text" lang={translationLanguageCode}>
              {englishTranslation}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
